<template>
  <div class="map-picker-modal" @click.self="$emit('cancel')">
    <div class="map-picker-content" ref="modalContent">
      <div class="map-header">
        <h3>📍 地图选点 / Pick Location on Map</h3>
        <button class="close-btn" @click="$emit('cancel')">×</button>
      </div>
      <div class="map-body">
        <div id="map-picker-container" ref="mapContainer" class="map-container"></div>
        <div class="map-info">
          <div class="info-row">
            <span class="label">地址 / Address:</span>
            <input
              v-model="selectedAddress"
              class="address-input"
              placeholder="点击地图选点后，可手动编辑地址"
            />
          </div>
          <div class="info-row">
            <span class="label">坐标 / Coordinates:</span>
            <span class="value">{{ selectedLat.toFixed(6) }}, {{ selectedLng.toFixed(6) }}</span>
          </div>
        </div>
      </div>
      <div class="map-footer">
        <button class="btn-secondary" @click="$emit('cancel')">取消 / Cancel</button>
        <button class="btn-primary" @click="confirmLocation" :disabled="!selectedAddress">
          确认位置 / Confirm
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default {
  name: 'MapPicker',
  props: {
    initialAddress: { type: String, default: '' },
    initialLat: { type: Number, default: 1.3521 },  // Singapore default
    initialLng: { type: Number, default: 103.8198 }
  },
  data() {
    return {
      map: null,
      marker: null,
      selectedAddress: '',
      selectedLat: 1.3521,
      selectedLng: 103.8198
    };
  },
  mounted() {
    // ⭐ 锁定背景滚动（移动端必需）
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    // ⭐ 恢复背景滚动
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  },
  methods: {
    initMap() {
      this.map = L.map(this.$refs.mapContainer, {
        tap: true,            // ⭐ 启用移动端触摸
        touchZoom: true,      // ⭐ 允许双指缩放
        dragging: true,       // ⭐ 允许拖动
        scrollWheelZoom: true
      }).setView(
        [this.initialLat, this.initialLng], 14
      );

      // Google 地图瓦片
      L.tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        attribution: '© Google Maps',
        maxZoom: 20
      }).addTo(this.map);

      // Initial marker if coordinates provided
      if (this.initialLat && this.initialLng) {
        this.marker = L.marker([this.initialLat, this.initialLng]).addTo(this.map);
      }

      // Click to select
      this.map.on('click', (e) => {
        this.onMapClick(e.latlng.lat, e.latlng.lng);
      });

      // Try reverse geocode on initial position
      if (this.initialLat && this.initialLng) {
        this.selectedLat = this.initialLat;
        this.selectedLng = this.initialLng;
        this.reverseGeocode(this.initialLat, this.initialLng);
      }

      // Try to get current location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.map.setView([pos.coords.latitude, pos.coords.longitude], 16);
            if (this.marker) this.marker.setLatLng([pos.coords.latitude, pos.coords.longitude]);
            else this.marker = L.marker([pos.coords.latitude, pos.coords.longitude]).addTo(this.map);
            this.selectedLat = pos.coords.latitude;
            this.selectedLng = pos.coords.longitude;
            this.reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          },
          () => {} // silent fail
        );
      }

      // Fix map rendering
      setTimeout(() => {
        this.map.invalidateSize();
      }, 100);
    },

    onMapClick(lat, lng) {
      this.selectedLat = lat;
      this.selectedLng = lng;

      if (this.marker) {
        this.marker.setLatLng([lat, lng]);
      } else {
        this.marker = L.marker([lat, lng]).addTo(this.map);
      }

      this.reverseGeocode(lat, lng);
    },

    async reverseGeocode(lat, lng) {
      // ⭐ 先用坐标作为占位，避免空白
      this.selectedAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

      // ⭐ 尝试 Nominatim 逆地理编码（获取实际地址）
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=zh`,
          { headers: { 'User-Agent': 'WholesaleSystem/1.0' } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data && data.display_name) {
          this.selectedAddress = data.display_name;
        }
      } catch (e) {
        console.warn('逆地理编码失败，使用坐标:', e);
        // 保持坐标作为地址，用户可手动编辑
      }
    },

    confirmLocation() {
      this.$emit('confirm', {
        address: this.selectedAddress,
        lat: this.selectedLat,
        lng: this.selectedLng
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.map-picker-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000000;
  /* ⭐ 阻止背景页面滚动，让地图自由拖动 */
  overflow: hidden;
}

/* ⭐ 阻止背景页面滚动 */
.map-picker-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.map-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #333;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #999;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;

    &:hover {
      background: #f5f5f5;
    }
  }
}

.map-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 12px;
}

.map-container {
  width: 100%;
  height: 300px;
  border-radius: 8px;
  z-index: 1;
  /* ⭐ Leaflet 移动端拖动必需 */
  touch-action: none;
}

/* ⭐ 移动端适配 */
@media (max-width: 768px) {
  .map-container {
    height: 250px;
  }

  .map-picker-content {
    width: 95%;
    max-height: 90vh;
  }

  .info-row {
    flex-direction: column;
    gap: 4px;
  }

  .label {
    min-width: auto !important;
  }

  .address-input {
    width: 100%;
  }
}

.map-info {
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;

  .info-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 13px;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      color: #666;
      min-width: 120px;
      font-weight: 500;
    }

    .value {
      color: #333;
      flex: 1;
      word-break: break-word;
    }

    .address-input {
      flex: 1;
      padding: 6px 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 13px;
      outline: none;

      &:focus {
        border-color: #667eea;
      }
    }
  }
}

.map-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;

  button {
    flex: 1;
    padding: 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s;

    &.btn-secondary {
      background: #e2e8f0;
      color: #4a5568;

      &:hover {
        background: #cbd5e0;
      }
    }

    &.btn-primary {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
